import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type TouchpointType =
  | 'message'
  | 'session'
  | 'check_in'
  | 'program_update'
  | 'note'
  | 'call'
  | 'feedback';

export type TouchpointInitiator = 'trainer' | 'client' | 'system';

export type GoalStatus = 'no_goal' | 'on_track' | 'minimum' | 'below_goal';

export interface TouchpointGoal {
  minWeekly: number;
  targetWeekly: number;
  alertThresholdDays: number;
}

export interface TouchpointStats {
  total: number;
  thisWeek: number;
  lastWeek: number;
  byType: Record<TouchpointType, number>;
  byInitiator: Record<TouchpointInitiator, number>;
  lastTouchpointDate: string | null;
  daysSinceContact: number | null;
  goal: TouchpointGoal | null;
  goalStatus: GoalStatus;
}

export interface Touchpoint {
  id: string;
  clientId: string;
  organizationId: string;
  touchpointType: TouchpointType;
  touchpointDate: string;
  initiatedBy: TouchpointInitiator;
  referenceId: string | null;
  referenceTable: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

interface UseTouchpointTrackingOptions {
  days?: number;
}

export function useTouchpointTracking(
  clientId: string | undefined,
  options: UseTouchpointTrackingOptions = {}
) {
  const { days = 30 } = options;
  const [stats, setStats] = useState<TouchpointStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    if (!clientId) {
      setStats(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the SQL function to get touchpoint stats
      const { data, error: queryError } = await supabase.rpc(
        'get_touchpoint_stats',
        {
          p_client_id: clientId,
          p_days: days,
        }
      );

      if (queryError) throw queryError;

      if (data) {
        setStats({
          total: data.total || 0,
          thisWeek: data.this_week || 0,
          lastWeek: data.last_week || 0,
          byType: data.by_type || {},
          byInitiator: data.by_initiator || {},
          lastTouchpointDate: data.last_touchpoint_date || null,
          daysSinceContact: data.days_since_contact || null,
          goal: data.goal
            ? {
                minWeekly: data.goal.min_weekly,
                targetWeekly: data.goal.target_weekly,
                alertThresholdDays: data.goal.alert_threshold_days,
              }
            : null,
          goalStatus: data.goal_status || 'no_goal',
        });
      }
    } catch (err) {
      console.error('Error loading touchpoint stats:', err);
      setError('Failed to load touchpoint stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, days]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return { stats, loading, error, reload: loadStats };
}

// Hook to fetch touchpoint history
export function useTouchpointHistory(
  clientId: string | undefined,
  options: UseTouchpointTrackingOptions = {}
) {
  const { days = 30 } = options;
  const [touchpoints, setTouchpoints] = useState<Touchpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTouchpoints = useCallback(async () => {
    if (!clientId) {
      setTouchpoints([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const startDateStr = startDate.toISOString().split('T')[0];

      const { data, error: queryError } = await supabase
        .from('client_touchpoints')
        .select('*')
        .eq('client_id', clientId)
        .gte('touchpoint_date', startDateStr)
        .order('touchpoint_date', { ascending: false });

      if (queryError) {
        // Table might not exist yet
        if (queryError.code === '42P01') {
          setTouchpoints([]);
          return;
        }
        throw queryError;
      }

      setTouchpoints(
        (data || []).map((t) => ({
          id: t.id,
          clientId: t.client_id,
          organizationId: t.organization_id,
          touchpointType: t.touchpoint_type as TouchpointType,
          touchpointDate: t.touchpoint_date,
          initiatedBy: t.initiated_by as TouchpointInitiator,
          referenceId: t.reference_id,
          referenceTable: t.reference_table,
          metadata: t.metadata || {},
          createdAt: t.created_at,
        }))
      );
    } catch (err) {
      console.error('Error loading touchpoint history:', err);
      setError('Failed to load touchpoint history');
      setTouchpoints([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, days]);

  useEffect(() => {
    loadTouchpoints();
  }, [loadTouchpoints]);

  return { touchpoints, loading, error, reload: loadTouchpoints };
}

// Hook to manage touchpoint goals
export function useTouchpointGoals(clientId: string | undefined) {
  const [goal, setGoal] = useState<TouchpointGoal | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadGoal = useCallback(async () => {
    if (!clientId) {
      setGoal(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: queryError } = await supabase
        .from('touchpoint_goals')
        .select('*')
        .eq('client_id', clientId)
        .single();

      if (queryError) {
        if (queryError.code === 'PGRST116' || queryError.code === '42P01') {
          setGoal(null);
          return;
        }
        throw queryError;
      }

      if (data) {
        setGoal({
          minWeekly: data.min_weekly_touchpoints,
          targetWeekly: data.target_weekly_touchpoints,
          alertThresholdDays: data.alert_threshold_days,
        });
      }
    } catch (err) {
      console.error('Error loading touchpoint goal:', err);
      setError('Failed to load touchpoint goal');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const saveGoal = useCallback(
    async (newGoal: TouchpointGoal) => {
      if (!clientId) return;

      setSaving(true);
      setError(null);

      try {
        // Get client's organization ID
        const { data: client, error: clientError } = await supabase
          .from('clients')
          .select('organization_id')
          .eq('id', clientId)
          .single();

        if (clientError) throw clientError;

        const { error: upsertError } = await supabase
          .from('touchpoint_goals')
          .upsert(
            {
              client_id: clientId,
              organization_id: client.organization_id,
              min_weekly_touchpoints: newGoal.minWeekly,
              target_weekly_touchpoints: newGoal.targetWeekly,
              alert_threshold_days: newGoal.alertThresholdDays,
            },
            { onConflict: 'client_id' }
          );

        if (upsertError) throw upsertError;

        setGoal(newGoal);
      } catch (err) {
        console.error('Error saving touchpoint goal:', err);
        setError('Failed to save touchpoint goal');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [clientId]
  );

  useEffect(() => {
    loadGoal();
  }, [loadGoal]);

  return { goal, loading, saving, error, reload: loadGoal, saveGoal };
}

// Function to manually log a touchpoint
export async function logTouchpoint(
  clientId: string,
  organizationId: string,
  type: TouchpointType,
  initiatedBy: TouchpointInitiator = 'trainer',
  metadata: Record<string, unknown> = {}
) {
  const { error } = await supabase.from('client_touchpoints').insert({
    client_id: clientId,
    organization_id: organizationId,
    touchpoint_type: type,
    touchpoint_date: new Date().toISOString().split('T')[0],
    initiated_by: initiatedBy,
    metadata,
  });

  if (error) {
    console.error('Error logging touchpoint:', error);
    throw error;
  }
}
